// src/components/layout/Layout.tsx
import React, { type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Sidebar from './Sidebar';
import Header from './Header';

interface LayoutProps {
  children: ReactNode;
  requiredRoles?: string[];
}

const Layout: React.FC<LayoutProps> = ({ children, requiredRoles = [] }) => {
  const { user, isLoading, hasRole } = useAuth();

  // Show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (requiredRoles.length > 0 && !requiredRoles.some(role => hasRole(role))) {
    // ADD DEBUG INFO HERE:
    console.log('🔍 ACCESS DENIED DEBUG INFO:');
    console.log('   requiredRoles:', requiredRoles);
    // console.log('   userRoles:', userRoles); // Assuming you have this variable
    console.log('   hasRole for each required role:');
    requiredRoles.forEach(role => {
      console.log(`     ${role}: ${hasRole(role)}`);
    });
    console.log('   Condition breakdown:');
    console.log(`     requiredRoles.length > 0: ${requiredRoles.length > 0}`);
    console.log(`     some(role => hasRole(role)): ${requiredRoles.some(role => hasRole(role))}`);
    console.log(`     Final condition: ${requiredRoles.length > 0 && !requiredRoles.some(role => hasRole(role))}`);
    console.log("*****************\n", user);

    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⛔</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">
            You don't have permission to access this page.
          </p>
          {/* ADD DEBUG PANEL */}
          <div className="mt-6 p-4 bg-gray-100 rounded-lg text-left max-w-md mx-auto">
            <h3 className="font-bold text-gray-800 mb-2">Debug Information:</h3>
            <div className="text-sm font-mono">
              <p><strong>Required Roles:</strong> {JSON.stringify(requiredRoles)}</p>
              {/* <p><strong>User Roles:</strong> {JSON.stringify(userRoles)}</p> */}
              <p><strong>hasRole('admin'):</strong> {hasRole('admin').toString()}</p>
              <p><strong>hasRole('manager'):</strong> {hasRole('manager').toString()}</p>
              <p><strong>hasRole('driver'):</strong> {hasRole('driver').toString()}</p>
              <p><strong>hasRole('customer'):</strong> {hasRole('customer').toString()}</p>
            </div>
            <button
              onClick={() => {
                console.log('Local Storage dump:');
                console.log('access_token:', localStorage.getItem('access_token')?.substring(0, 50) + '...');
                console.log('user:', localStorage.getItem('user'));
                console.log('roles:', localStorage.getItem('roles'));

                // Force reload with cleared storage if needed
                // localStorage.clear();
                // window.location.reload();
              }}
              className="mt-3 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm"
            >
              Debug Local Storage
            </button>
          </div>
        </div>
      </div>
    );
  }
  // // Check role permissions
  // if (requiredRoles.length > 0 && !requiredRoles.some(role => hasRole(role))) {
  //   return (
  //     <div className="min-h-screen flex items-center justify-center">
  //       <div className="text-center">
  //         <div className="text-red-500 text-6xl mb-4">⛔</div>
  //         <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
  //         <p className="text-gray-600">
  //           You don't have permission to access this page.
  //         </p>
  //       </div>
  //     </div>
  //   );
  // }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="flex">
        <Sidebar />
        <main className="flex-1 p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;